<template>
  <section class="new-message" aria-label="New message">
    <header class="new-message__header">
      <SquarePen :size="16" class="tw-text-primary" />
      <span class="new-message__title">New message</span>
      <div class="tw-flex-grow" />
      <button type="button" class="new-message__close" title="Close" @click="closeComposer"><X :size="15" /></button>
    </header>

    <div class="new-message__body">
      <label class="new-message__label" for="new-message-to">To</label>
      <el-select
        id="new-message-to"
        v-model="recipientId"
        filterable
        remote
        clearable
        :remote-method="searchPatients"
        :loading="isSearchingPatients"
        placeholder="Patient name"
        no-data-text="No patients found"
        class="tw-w-full"
      >
        <el-option v-for="option in patientOptions" :key="option.id" :value="option.id" :label="option.label" />
      </el-select>

      <el-alert
        v-if="eligibility && !eligibility.eligible"
        type="warning"
        show-icon
        :closable="false"
        :title="describeIneligibilityReason(eligibility.reason)"
        class="tw-mt-2"
      />

      <label class="new-message__label" for="new-message-subject">Subject</label>
      <el-select id="new-message-subject" v-model="subject" class="tw-w-full">
        <el-option v-for="option in NEW_MESSAGE_SUBJECTS" :key="option" :value="option" :label="option" />
      </el-select>

      <label class="new-message__label" for="new-message-content">Message</label>
      <el-input
        id="new-message-content"
        v-model="content"
        type="textarea"
        :rows="5"
        :maxlength="MAX_CONTENT_CHARS"
        placeholder="Write a message…"
        resize="none"
      />

      <label class="new-message__label">Assignee</label>
      <StaffPickerDropdown
        :staff="staffDirectory ?? []"
        :loading="isStaffDirectoryPending"
        @select="onAssigneeSelected"
      >
        <button type="button" class="new-message__assignee" aria-haspopup="dialog">
          <template v-if="assignee">
            <StaffInitialsAvatar :full-name="assignee.fullName" />
            <span class="tw-min-w-0 tw-flex-grow">
              <span class="new-message__assignee-name">{{ assignee.fullName }}</span>
              <span class="new-message__assignee-role">{{ roleLabel(assignee.role) }}</span>
            </span>
          </template>
          <span v-else class="new-message__assignee-placeholder">Choose assignee</span>
          <ChevronDown :size="16" class="new-message__assignee-chevron" />
        </button>
      </StaffPickerDropdown>

      <el-checkbox v-model="urgent" class="tw-mt-3">Mark as urgent</el-checkbox>
    </div>

    <footer class="new-message__footer">
      <el-button @click="closeComposer">Cancel</el-button>
      <el-button type="primary" :disabled="!canSend" :loading="isSending" @click="send">Send</el-button>
    </footer>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { ElMessage } from "element-plus"
import { ChevronDown, SquarePen, X } from "lucide-vue-next"
import { DateTime } from "luxon"
import { PatientConversationRoleEnum, PatientConversationUrgencyEnum } from "@vitable/asclepius-api-client-ts"
import { useStore } from "@/store"
import { useSearchMembers } from "@/composables/member"
import { toPrefixedId } from "@/utils/prefixed-id"
import StaffInitialsAvatar from "@/modules/patient-conversations/components/StaffInitialsAvatar.vue"
import StaffPickerDropdown from "@/modules/patient-conversations/components/StaffPickerDropdown.vue"
import { useCurrentStaffId } from "@/modules/patient-conversations/composables/current-staff"
import { usePatientConversationPanel } from "@/modules/patient-conversations/composables/patient-conversation-panel"
import {
  usePatientConversationEligibility,
  useStartPatientConversation
} from "@/modules/patient-conversations/composables/patient-conversations"
import { usePatientConversationStaffDirectory } from "@/modules/patient-conversations/composables/staff-directory"
import {
  describeIneligibilityReason,
  describePatientConversationError
} from "@/modules/patient-conversations/constants/patient-conversation-error-codes"
import { NEW_MESSAGE_SUBJECTS, roleLabel } from "@/modules/patient-conversations/constants/patient-conversation-labels"

// TDP §5.2.3
const MAX_CONTENT_CHARS = 4000

interface PatientOption {
  /** `mbr_…` */
  id: string
  label: string
}

const store = useStore()
const currentStaffId = useCurrentStaffId()
const { composerRecipient, closeComposer, openConversation } = usePatientConversationPanel()

// The chart's "Message patient" pre-selects its patient; the inbox's compose button starts blank
const recipientId = ref<string | undefined>(composerRecipient.value?.id)
const knownPatients = ref<Array<PatientOption>>(
  composerRecipient.value ? [{ id: composerRecipient.value.id, label: composerRecipient.value.fullName }] : []
)
const subject = ref<string>(NEW_MESSAGE_SUBJECTS[0])
const content = ref<string>("")
const urgent = ref<boolean>(false)
// TDP §5.1.2: pre-filled with the author, who may hand the conversation to someone else from the start
const assigneeId = ref<string | undefined>(currentStaffId.value)

const { query: memberSearch, name: memberSearchName } = useSearchMembers({ limit: 10 })
const isSearchingPatients = computed<boolean>(() => memberSearch.isFetching.value)
const patientOptions = computed<Array<PatientOption>>(() => {
  const found: Array<PatientOption> = (memberSearch.data.value?.results ?? []).map((member) => ({
    id: toPrefixedId("mbr", member.id),
    label: `${member.full_name}, DOB ${DateTime.fromISO(member.date_of_birth).toFormat("M/d/yyyy")}`
  }))
  // keep the chosen patient listed while a new search narrows the options away from them
  const chosen = knownPatients.value.filter(
    (known) => known.id === recipientId.value && !found.some((f) => f.id === known.id)
  )
  return [...chosen, ...found]
})

function searchPatients(query: string): void {
  memberSearchName.value = query.trim() || undefined
}

watch(recipientId, (id) => {
  const option = patientOptions.value.find((candidate) => candidate.id === id)
  if (option) knownPatients.value = [option]
})

const { data: eligibility, isFetching: isCheckingEligibility } = usePatientConversationEligibility(recipientId)

const { data: staffDirectory, isPending: isStaffDirectoryPending } = usePatientConversationStaffDirectory()
const assignee = computed<{ fullName: string; role: PatientConversationRoleEnum } | undefined>(() => {
  if (!assigneeId.value) return undefined
  if (assigneeId.value === currentStaffId.value) {
    return {
      fullName: store.getters["me/user"]?.provider?.full_name ?? "Me",
      role: store.getters["me/isClinicalAdmin"]
        ? PatientConversationRoleEnum.SupportTeam
        : PatientConversationRoleEnum.Provider
    }
  }
  const person = staffDirectory.value?.find((candidate) => candidate.id === assigneeId.value)
  return person ? { fullName: person.fullName, role: person.role } : undefined
})

function onAssigneeSelected(staffId: string | null): void {
  if (staffId) assigneeId.value = staffId
}

const { mutateAsync: startConversation, isPending: isSending } = useStartPatientConversation()

const canSend = computed<boolean>(
  () =>
    !!recipientId.value &&
    !!assigneeId.value &&
    content.value.trim().length > 0 &&
    eligibility.value?.eligible === true &&
    !isCheckingEligibility.value &&
    !isSending.value
)

async function send(): Promise<void> {
  if (!canSend.value || !recipientId.value || !assigneeId.value) return
  try {
    const conversation = await startConversation({
      member_id: recipientId.value,
      subject: subject.value,
      urgency: urgent.value ? PatientConversationUrgencyEnum.Urgent : PatientConversationUrgencyEnum.Normal,
      assignee_id: assigneeId.value,
      message: { content: content.value.trim() }
    })
    ElMessage.success(`Message sent to ${conversation.member?.full_name ?? "the patient"}`)
    closeComposer()
    openConversation(conversation.id)
  } catch (error) {
    ElMessage.error(describePatientConversationError(error))
  }
}
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.new-message
  display: flex
  flex-direction: column
  width: 420px
  max-width: calc(100vw - 40px)
  max-height: calc(100vh - 100px)
  background-color: white
  border: 1px solid $border
  border-radius: 12px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15)
  overflow: hidden

  &__header
    display: flex
    align-items: center
    gap: 8px
    padding: 14px 16px
    border-bottom: 1px solid $border

  &__title
    font-size: 15px
    font-weight: 700
    color: $empty-text

  &__close
    display: inline-flex
    align-items: center
    justify-content: center
    width: 28px
    height: 28px
    border: 0
    border-radius: 6px
    background: transparent
    color: $grey
    cursor: pointer

    &:hover
      background-color: $grey-light
      color: $empty-text

  &__body
    display: flex
    flex-direction: column
    padding: 12px 16px 4px
    overflow-y: auto

  &__label
    display: block
    margin: 10px 0 4px
    font-size: 13px
    font-weight: 600
    color: $empty-text

    &:first-child
      margin-top: 0

  // Styled like el-select so the field reads as part of the form
  &__assignee
    display: flex
    align-items: center
    gap: 10px
    width: 100%
    padding: 6px 12px
    border: 1px solid var(--el-border-color)
    border-radius: var(--el-border-radius-base)
    background-color: white
    text-align: left
    cursor: pointer
    transition: border-color 0.15s, box-shadow 0.15s

    &:hover
      border-color: var(--el-border-color-hover)

    &:focus-visible
      outline: 0
      border-color: $primary
      box-shadow: 0 0 0 3px $highlight-light

  &__assignee-name
    display: block
    font-size: 14px
    line-height: 1.3
    color: $empty-text
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__assignee-role
    display: block
    font-size: 12px
    line-height: 1.3
    color: $grey

  &__assignee-placeholder
    flex: 1 1 auto
    font-size: 14px
    color: var(--el-text-color-placeholder)

  &__assignee-chevron
    flex: none
    color: $grey

  &__footer
    display: flex
    justify-content: flex-end
    gap: 8px
    padding: 12px 16px
    border-top: 1px solid $border
</style>
